import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Site, FilterType } from '../types';

interface SitesTableProps {
    sites: Site[];
    filter: FilterType;
    onFilterChange: (filter: FilterType) => void;
}

const statusConfig = {
    up: { 
        label: 'Работает', 
        shortLabel: 'OK',
        className: 'bg-[var(--status-up-bg)] text-[var(--status-up-text)]', 
        dot: 'bg-[var(--status-up)]' 
    },
    warn: { 
        label: 'Проблемы', 
        shortLabel: '!',
        className: 'bg-[var(--status-warn-bg)] text-[var(--status-warn-text)]', 
        dot: 'bg-[var(--status-warn)]' 
    },
    down: { 
        label: 'Не работает', 
        shortLabel: '✕',
        className: 'bg-[var(--status-down-bg)] text-[var(--status-down-text)]', 
        dot: 'bg-[var(--status-down)]' 
    }
};

const filters: { key: FilterType; label: string }[] = [
    { key: 'all', label: 'Все' },
    { key: 'up', label: 'Работают' },
    { key: 'warn', label: 'Проблемы' },
    { key: 'down', label: 'Не работают' }
];

const formatLocalDateTime = (timestamp: number): string => {
    if (!timestamp || isNaN(timestamp)) return '—';

    const date = new Date(timestamp);

    return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
    }).format(date);
};

const formatCheckedAgo = (timestamp: number): string => {
    if (!timestamp || isNaN(timestamp)) return '—';
    
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 5) return 'только что';
    if (seconds < 60) return `${seconds} сек`;

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} мин`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} ч ${minutes % 60} мин`;

    const days = Math.floor(hours / 24);
    return `${days} д ${hours % 24} ч`;
};

export const SitesTable: React.FC<SitesTableProps> = ({ sites, filter, onFilterChange }) => {
    return (
        <div className="w-full">
            <div className="flex flex-wrap items-center gap-1.5 px-[18px] mb-3">
                {filters.map(f => (
                    <button
                        key={f.key}
                        onClick={() => onFilterChange(f.key)}
                        className={`
                            px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200
                            ${filter === f.key
                                ? 'bg-[var(--brand)] border-[var(--brand)] text-white shadow-sm'
                                : 'bg-[var(--bg-elevated)] border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--border-strong)] hover:text-[var(--text)]'
                            }
                        `}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <div className="mx-[18px] bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl overflow-hidden shadow-sm">
                <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[minmax(0,1fr)_120px_1fr] gap-x-3 sm:gap-x-4 px-3 sm:px-4 py-3 bg-[var(--bg)] border-b border-[var(--border)] text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider items-center">
                    <span className="min-w-0">Сайт</span>
                    <span className="text-center">Состояние</span>
                    <span className="text-right hidden sm:block">
                        {filter === 'up' || filter === 'all' ? 'Проверен' : 'Упал'}
                    </span>
                </div>

                <div className="divide-y divide-[var(--border)]">
                    <AnimatePresence mode="popLayout" initial={false}>
                        {sites.length === 0 ? (
                            <motion.div
                                key="empty"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                transition={{ duration: 0.2 }}
                                className="flex flex-col items-center justify-center py-12 px-4 text-center"
                            >
                                <div className="w-11 h-11 rounded-xl bg-[var(--bg)] border border-[var(--border)] flex items-center justify-center text-[var(--text-muted)] mb-3">
                                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <circle cx="12" cy="12" r="10"/>
                                        <line x1="8" y1="12" x2="16" y2="12"/>
                                    </svg>
                                </div>
                                <p className="text-sm font-medium text-[var(--text)] mb-1">Нет сайтов</p>
                                <p className="text-xs text-[var(--text-muted)]">Попробуйте другой фильтр</p>
                            </motion.div>
                        ) : (
                            sites.map((site, index) => {
                                const status = statusConfig[site.status];
                                const isProblem = site.status !== 'up';
                                
                                return (
                                    <motion.div
                                        key={site.id}
                                        layout
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ 
                                            duration: 0.2, 
                                            delay: Math.min(index * 0.03, 0.3),
                                            layout: { duration: 0.2 }
                                        }}
                                        className="grid grid-cols-[1fr_auto] sm:grid-cols-[minmax(0,1fr)_120px_1fr] gap-x-3 sm:gap-x-4 px-3 sm:px-4 py-3.5 items-center hover:bg-[var(--bg-hover)] transition-colors cursor-pointer group"
                                    >
                                        <div className="min-w-0 overflow-hidden">
                                            <div 
                                                className="font-medium text-[13px] text-[var(--text)] mb-0.5 truncate"
                                                title={site.name}
                                            >
                                                {site.name}
                                            </div>
                                            <a 
                                                href={site.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className="text-[11px] text-[var(--text-muted)] font-mono truncate block hover:text-[var(--brand)] transition-colors"
                                                title={site.url}
                                            >
                                                {site.url.replace('https://', '')}
                                            </a>
                                        </div>

                                        <div className="flex justify-center">
                                            <span 
                                                className={`
                                                    inline-flex items-center gap-1.5 px-2 py-1 sm:px-2.5 sm:py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap
                                                    ${status.className}
                                                `}
                                                title={status.label}
                                            >
                                                <span className={`w-1.5 h-1.5 rounded-full ${status.dot} flex-shrink-0`} />
                                                <span className="hidden sm:inline">{status.label}</span>
                                                <span className="sm:hidden">{status.shortLabel}</span>
                                            </span>
                                        </div>

                                        <div className="text-right hidden sm:block">
                                            {isProblem ? (
                                                <div className="flex flex-col items-end gap-0.5">
                                                    <span 
                                                        className="text-[11px] text-[var(--status-down)] font-medium whitespace-nowrap"
                                                        title={site.problemSince ? formatLocalDateTime(site.problemSince) : '—'}
                                                    >
                                                        {site.problemSince 
                                                            ? formatCheckedAgo(site.problemSince) 
                                                            : '—'}
                                                    </span>
                                                    <span className="text-[10px] text-[var(--text-muted)] font-mono">
                                                        {site.problemSince 
                                                            ? formatLocalDateTime(site.problemSince)
                                                            : '—'}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-[11px] text-[var(--text-muted)]">—</span>
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })
                        )}
                    </AnimatePresence>
                </div>
            </div>
            
            {sites.length > 0 && (
                <div className="px-[18px] mt-2 text-[11px] text-[var(--text-muted)]">
                    Показано {sites.length} {sites.length === 1 ? 'сайт' : (sites.length > 0 && sites.length < 5) ? 'сайта' : 'сайтов'}
                </div>
            )}
        </div>
    );
};